import {
  DEFAULT_SECONDARY_HARMONY,
  formatHex,
  generateStorefrontPalette,
  hexToOklch,
  isHexColor,
  paletteContrastReport,
  parseHex,
  SECONDARY_HARMONIES,
  type SecondaryHarmony,
  STOREFRONT_TOKEN_DEFAULTS,
  STOREFRONT_TOKEN_GROUPS,
  STOREFRONT_TOKEN_KEYS,
  type StorefrontPalette,
  type StorefrontTokenKey,
} from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@loot-os/ui/components/tooltip";
import { ArrowRight } from "lucide-react";
import { useMemo, useState } from "react";

import { ColorField, Swatch } from "./StorefrontFields";
import { StorefrontThemeScope } from "./StorefrontThemeScope";

const HEX6 = /^#[0-9a-fA-F]{6}$/;

/** The token's saved value if the color picker can show it, else the stock one. */
function pickerSafe(saved: StorefrontPalette, key: StorefrontTokenKey): string {
  return HEX6.test(saved[key]) ? saved[key] : STOREFRONT_TOKEN_DEFAULTS[key];
}

/** `#FFF`, `#ffffff` and `#FFFFFF` are one color; anything non-hex compares as written. */
function canonical(value: string): string {
  const rgb = parseHex(value);
  return rgb ? formatHex(rgb) : value.trim();
}

const CHART_KEYS = ["chart-1", "chart-2", "chart-3", "chart-4", "chart-5"] as const;

/**
 * Three brand colors in, a whole palette saved. The seeds live here only: the
 * button writes the generated tokens to the storefront theme through `onSave`,
 * and the "changes on save" list shows exactly which variables that touches
 * against what is stored now, so saving is never a surprise.
 *
 * Blank primary and background fields mean "the saved value" (their
 * placeholder), so the card always previews something. A blank secondary
 * means "derive one" — the harmony picker decides how, triadic by default.
 */
export function StorefrontPaletteGenerator({
  saved,
  radius,
  fontFamily,
  canEdit,
  saving,
  onSave,
}: {
  /** The storefront's palette as stored — what a save is compared against. */
  saved: StorefrontPalette;
  radius?: string;
  fontFamily?: string;
  canEdit: boolean;
  saving: boolean;
  onSave: (tokens: StorefrontPalette) => void;
}) {
  const [primary, setPrimary] = useState("");
  const [background, setBackground] = useState("");
  // Seeded with the saved secondary only when it is a real color of its own;
  // one equal to the muted surface is the derived kind and stays blank.
  const [secondary, setSecondary] = useState(() =>
    saved.secondary !== saved.muted && HEX6.test(saved.secondary) ? saved.secondary : "",
  );
  const [harmony, setHarmony] = useState<SecondaryHarmony>(DEFAULT_SECONDARY_HARMONY);

  const primarySeed = primary.trim() || pickerSafe(saved, "primary");
  const backgroundSeed = background.trim() || pickerSafe(saved, "background");
  const secondarySeed = secondary.trim();

  const preview = useMemo(() => {
    if (!isHexColor(primarySeed) || !isHexColor(backgroundSeed)) return null;
    if (secondarySeed && !isHexColor(secondarySeed)) return null;
    return generateStorefrontPalette({
      primary: primarySeed,
      secondary: secondarySeed || undefined,
      background: backgroundSeed,
      harmony,
    });
  }, [primarySeed, secondarySeed, backgroundSeed, harmony]);

  const report = useMemo(() => (preview ? paletteContrastReport(preview) : []), [preview]);
  const changes = useMemo(
    () =>
      preview
        ? STOREFRONT_TOKEN_KEYS.filter((key) => canonical(preview[key]) !== canonical(saved[key]))
        : [],
    [preview, saved],
  );
  const backgroundL = preview ? hexToOklch(preview.background)?.L : undefined;
  const midLightness = backgroundL !== undefined && backgroundL > 0.4 && backgroundL < 0.7;

  const canSave = preview !== null && changes.length > 0 && canEdit && !saving;

  return (
    <div className="grid gap-6">
      {/* Seeds stack in a fixed-width column so labels stay next to their inputs;
          the generated palette fills the rest of the row beside them. */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:gap-0 lg:divide-x max-lg:divide-y">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 lg:pr-10">
          <ColorField
            id="palette-seed-primary"
            label="Primary"
            hint="buttons, links"
            fallback={pickerSafe(saved, "primary")}
            value={primary}
            onChange={setPrimary}
          />
          <ColorField
            id="palette-seed-background"
            label="Background"
            hint="page"
            fallback={pickerSafe(saved, "background")}
            value={background}
            onChange={setBackground}
          />
          <ColorField
            id="palette-seed-secondary"
            label="Secondary"
            hint="optional"
            fallback={preview?.secondary ?? pickerSafe(saved, "secondary")}
            value={secondary}
            onChange={setSecondary}
          />
          <div className="grid gap-2">
            <div className="flex items-baseline justify-between gap-2">
              <Label htmlFor="palette-harmony">Secondary when blank</Label>
              <code className="font-mono text-xs text-muted-foreground">harmony</code>
            </div>
            <Select
              value={harmony}
              onValueChange={(value) => setHarmony(value as SecondaryHarmony)}
              disabled={secondarySeed !== ""}
            >
              <SelectTrigger id="palette-harmony" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SECONDARY_HARMONIES.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {secondarySeed
                ? "Using the secondary you entered."
                : SECONDARY_HARMONIES.find((option) => option.id === harmony)?.description}
            </p>
          </div>
        </div>

        {preview ? (
          /* One row per token group, each stretched to share the seed column's
             height and fill its width, so the two halves form one rectangle. */
          <TooltipProvider>
            <div className="grid auto-rows-fr gap-2 max-lg:pt-6 lg:pl-10">
              {STOREFRONT_TOKEN_GROUPS.map((group) => (
                <div key={group.label} className="grid grid-cols-[4rem_minmax(0,1fr)] gap-3">
                  {/* The label's tooltip lists the group's variables, one per swatch, left to right. */}
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <span className="cursor-default self-center text-xs text-muted-foreground" />
                      }
                    >
                      {group.label}
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <span className="grid gap-0.5 font-mono">
                        {group.tokens.map((token) => (
                          <span key={token.key}>--{token.key}</span>
                        ))}
                      </span>
                    </TooltipContent>
                  </Tooltip>
                  <span
                    className="grid min-h-8 gap-1.5"
                    style={{
                      gridTemplateColumns: `repeat(${group.tokens.length}, minmax(0, 1fr))`,
                    }}
                  >
                    {group.tokens.map((token) => (
                      <Tooltip key={token.key}>
                        <TooltipTrigger
                          render={
                            <span
                              role="img"
                              aria-label={`--${token.key} ${preview[token.key]}`}
                              className="rounded-md ring-1 ring-border"
                              style={{ backgroundColor: preview[token.key] }}
                            />
                          }
                        />
                        <TooltipContent>
                          <span className="font-mono">--{token.key}</span>
                          <span className="font-mono opacity-70">{preview[token.key]}</span>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </TooltipProvider>
        ) : (
          <p className="text-sm text-destructive max-lg:pt-6 lg:pl-10">
            Enter hex colors (#rrggbb) for the primary, background and secondary to preview a
            palette.
          </p>
        )}
      </div>

      {preview && (
        <>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,24rem)]">
            <StorefrontThemeScope
              theme={{ tokens: preview, radius, fontFamily }}
              className="rounded-lg p-4 ring-1 ring-border"
            >
              <Card>
                <CardHeader>
                  <CardTitle>Mystery box</CardTitle>
                  <CardDescription>
                    How cards, buttons and text read in this palette.
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-3">
                  <div className="flex flex-wrap gap-2">
                    <Button type="button">Open box</Button>
                    <Button type="button" variant="secondary">
                      Details
                    </Button>
                    <Button type="button" variant="ghost">
                      Odds
                    </Button>
                    <Button type="button" variant="destructive">
                      Remove
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge>Legendary</Badge>
                    <Badge variant="secondary">Rare</Badge>
                    <Badge variant="outline">Common</Badge>
                  </div>
                  <Input readOnly placeholder="Search boxes…" aria-label="Preview input" />
                  <p className="text-sm text-muted-foreground">
                    1,204 opened this week · muted text on the card surface.
                  </p>
                  <div className="flex gap-1" aria-hidden>
                    {CHART_KEYS.map((key) => (
                      <span
                        key={key}
                        className="h-2 flex-1 rounded-full"
                        style={{ backgroundColor: preview[key] }}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </StorefrontThemeScope>

            <div className="grid min-w-0 content-start gap-1.5 text-sm">
              <span className="text-xs text-muted-foreground">Contrast (WCAG 2)</span>
              {report.map((row) => (
                <div
                  key={`${row.fg}/${row.bg}`}
                  className="flex min-w-0 items-center justify-between gap-3"
                >
                  <code className="min-w-0 truncate font-mono text-xs text-muted-foreground">
                    {row.fg} / {row.bg}
                  </code>
                  <span className="flex shrink-0 items-baseline gap-2 text-xs tabular-nums">
                    {Number.isNaN(row.ratio) ? "—" : row.ratio.toFixed(2)}
                    <span
                      className={`w-14 text-right ${
                        row.pass ? "text-muted-foreground" : "font-medium text-destructive"
                      }`}
                    >
                      {row.pass ? (row.min === 4.5 ? "AA" : "AA large") : `< ${row.min}`}
                    </span>
                  </span>
                </div>
              ))}
              {midLightness && (
                <p className="pt-1 text-xs text-muted-foreground">
                  A mid-lightness background can't reach AA text contrast with any color — go darker
                  or lighter for readable text.
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-2">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium">Changes on save</span>
              <span className="text-xs text-muted-foreground">
                {changes.length === 0
                  ? "Matches the saved palette"
                  : `${changes.length} of ${STOREFRONT_TOKEN_KEYS.length} variables`}
              </span>
            </div>
            {changes.length > 0 &&
              STOREFRONT_TOKEN_GROUPS.map((group) => {
                const changed = group.tokens.filter((token) => changes.includes(token.key));
                if (changed.length === 0) return null;
                return (
                  <div key={group.label} className="grid gap-1.5 border-t pt-2 first:border-t-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-xs font-medium">{group.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {changed.length} of {group.tokens.length}
                      </span>
                    </div>
                    <ul className="grid gap-x-8 gap-y-1.5 sm:grid-cols-2">
                      {changed.map(({ key }) => (
                        <li key={key} className="flex min-w-0 items-center gap-2 text-xs">
                          <Swatch color={saved[key]} title={`current ${saved[key]}`} />
                          <ArrowRight
                            className="size-3 shrink-0 text-muted-foreground"
                            aria-hidden
                          />
                          <Swatch color={preview[key]} title={`new ${preview[key]}`} />
                          <code className="min-w-0 truncate font-mono">--{key}</code>
                          <span className="ml-auto shrink-0 font-mono text-muted-foreground">
                            {preview[key]}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
          </div>
        </>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          size="sm"
          disabled={!canSave}
          onClick={() => preview && onSave(preview)}
        >
          {saving ? "Saving…" : "Save palette"}
        </Button>
        <span className="text-xs text-muted-foreground">
          {changes.length === 0 && preview
            ? "Nothing to save — this palette is already live."
            : "Saves the variables above to this storefront."}
        </span>
      </div>
    </div>
  );
}
